"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { userIdentifierAtom } from "@plura/client/hooks";
import { fireFormConversion, getCountryCode, isGDPRCountry, resetDefaultValuesToBlank } from "@plura/client/utils";
import { estimateCompletionSeconds, formatCompletionEstimate, getNextJourneyItem, getPreviousJourneyItem } from "@plura/shared/lead-forms";
import { events } from "@plura/shared/track";
import { trpc } from "@plura/shared/trpc";
import { Conversation, FormJourneyItem, JourneyItemMediaPosition, LeadForm, Media, ResponseMessages } from "@plura/shared/types";
import { AppError, cn, contrastingTextColor, env, numberRegex, phoneRegex } from "@plura/shared/utils";
import * as Sentry from '@sentry/nextjs';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { AnimatePresence, motion } from "framer-motion";
import { useAtom } from "jotai";
import debounce from "lodash.debounce";
import { useParams, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';
import { z } from "zod";
import { Form } from "../../atoms/Form";
import Icon from "../../atoms/Icon";
import Logo from "../../atoms/Logo";
import ConversationPagination from "../../molecules/ConversationPagination";
import ConversationStepWrapper from "../../molecules/ConversationStepWrapper";
import ContinueScreen from "./_conversationScreens/ContinueScreen";
import ConversationWelcomeScreen from "./_conversationScreens/ConversationWelcomeScreen";
import ScreenRenderer from "./ScreenRendering";
import "./conversation-wrapper.css";
import "./_conversationScreens/conversation-screens-base.css";

interface Props {
  leadForm: LeadForm
  currentStepOverride: string | null
  revisitingConversation?: boolean
  editorMode?: boolean;
  previewMode?: boolean;
  startingStep?: string;
  aiPreviewMode?: boolean;
  defaultValues: Record<string, any>;
  fontsLoaded: boolean;
  previousConversation?: Conversation | null;
  wrapperId?: string
  onContentClick?: (fieldName: string, blockId: string) => void;
  minimizeContentSize?: boolean;
  country?: string | null;
  onFinish?: (formValues: Record<string, any>) => (void | Promise<void>);
}

/** PostHog: UI hit a terminal step (even if finalize/summarize never runs). */
function trackPublicFormEndReached(input: {
  collectionId: string;
  conversationId?: string;
  currentStepType: string;
  nextJourneyItem: FormJourneyItem | null | undefined;
  navLeadForm: LeadForm;
  userResponses: Record<string, string>;
}) {
  const { nextJourneyItem } = input;
  if (!nextJourneyItem) {
    events.formEndReached({
      collectionId: input.collectionId,
      conversationId: input.conversationId,
      via: input.currentStepType === 'WELCOME_SCREEN' ? 'welcome_terminal' : 'exit',
      stepType: input.currentStepType,
    });
    return;
  }
  if (nextJourneyItem.type === 'END_SCREEN') {
    events.formEndReached({
      collectionId: input.collectionId,
      conversationId: input.conversationId,
      via: 'end_screen',
      stepType: nextJourneyItem.type,
    });
    return;
  }
  if (nextJourneyItem.type === 'REDIRECT_SCREEN') {
    events.formEndReached({
      collectionId: input.collectionId,
      conversationId: input.conversationId,
      via: 'redirect_screen',
      stepType: nextJourneyItem.type,
    });
    return;
  }
  // Last question → terminal welcome (no further question after it).
  if (nextJourneyItem.type === 'WELCOME_SCREEN') {
    const afterWelcome = getNextJourneyItem({
      leadForm: input.navLeadForm,
      currentJourneyItemId: nextJourneyItem.id,
      userResponses: input.userResponses,
    });
    if (
      !afterWelcome.nextJourneyItem ||
      afterWelcome.nextJourneyItem.type === 'END_SCREEN' ||
      afterWelcome.nextJourneyItem.type === 'REDIRECT_SCREEN'
    ) {
      events.formEndReached({
        collectionId: input.collectionId,
        conversationId: input.conversationId,
        via: 'welcome_terminal',
        stepType: 'WELCOME_SCREEN',
      });
    }
  }
}

const ConversationWrapper = ({ leadForm, currentStepOverride, revisitingConversation, editorMode, previewMode, aiPreviewMode, defaultValues, previousConversation, wrapperId = 'conversation-wrapper', onContentClick, minimizeContentSize = false, country, onFinish }: Props) => {
  const [userIdentifier, setUserIdentifier] = useAtom(userIdentifierAtom)
  const isHandlingAnswerRef = useRef(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const previousStepOverrideRef = useRef<string | null>(null);
  const transitionNodeRef = useRef<HTMLDivElement>(null);
  // Children of MULTI_QUESTION_PAGE containers don't appear as standalone steps in the
  // form's progression — the container itself is the rendered step. Navigation, totals,
  // and progress all run against the top-level list. The full journeyItems list is still
  // used for validation and for the child renderer inside the page screen.
  const topLevelJourneyItems = useMemo(
    () => (leadForm.journeyItems ?? []).filter(i => !i.parentId).sort((a, b) => a.order - b.order),
    [leadForm.journeyItems]
  )
  const navLeadForm = useMemo(
    () => ({
      ...leadForm,
      // getNextJourneyItem matches on `order + 1`; reindex top-level so adjacency holds.
      journeyItems: topLevelJourneyItems.map((item, idx) => ({ ...item, order: idx })),
    }),
    [leadForm, topLevelJourneyItems]
  )

  const [currentStepId, setCurrentStepId] = useState(() => {
    // When resuming an in-progress submission, start on the question the
    // respondent last reached rather than page 1. Their earlier answers are
    // already pre-filled (defaultValues), so jumping back to the start would
    // force them to re-click every answered step — re-saving each one and
    // creating duplicate answers. A fresh visit (no previousConversation)
    // falls through to the first step as before.
    const items = leadForm.journeyItems ?? []
    const firstBlockId = topLevelJourneyItems[0]?.blockId
    if (!previousConversation?.conversationItem?.length) return firstBlockId

    const lastShownJourneyItemId = [...previousConversation.conversationItem]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .find(item => item.role === 'Assistant')?.journeyItemId

    const resumeItem = items.find(i => i.id === lastShownJourneyItemId)
    if (!resumeItem) return firstBlockId

    // A child of a MULTI_QUESTION_PAGE isn't a standalone step — resume on its
    // parent container so navigation/progress stay valid.
    const navItem = resumeItem.parentId ? items.find(i => i.id === resumeItem.parentId) : resumeItem
    return navItem?.blockId ?? firstBlockId
  })
  // Ordered top-level blockIds the respondent has moved forward past this
  // session — Back pops this stack. Recomputing "previous" from form
  // structure alone can't tell which of several converging branches the user
  // actually took. Seeded from the saved conversation when resuming.
  const [stepHistory, setStepHistory] = useState<string[]>(() => {
    const conversationItems = previousConversation?.conversationItem
    if (!conversationItems?.length) return []
    const items = leadForm.journeyItems ?? []
    const visited: string[] = []
    for (const ci of [...conversationItems].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())) {
      if (ci.role !== 'Assistant') continue
      const item = items.find(i => i.id === ci.journeyItemId)
      if (!item) continue
      const blockId = (item.parentId ? items.find(i => i.id === item.parentId) : item)?.blockId
      if (blockId && visited[visited.length - 1] !== blockId) visited.push(blockId)
    }
    // History holds only the steps before the resume step being shown.
    const resumeIndex = visited.lastIndexOf(currentStepId)
    return resumeIndex >= 0 ? visited.slice(0, resumeIndex) : visited
  })
  const pushStepHistory = useCallback((blockId: string) => {
    setStepHistory(history => history[history.length - 1] === blockId ? history : [...history, blockId])
  }, [])
  // Missing on pre-migration rows / fixtures = the historical top-left spot.
  const logoPosition = leadForm.logoPosition ?? 'TOP_LEFT'

  const [classNameState, setClassNameState] = useState(
    leadForm.transitionType === 'SLIDE_HORIZONTAL' ? 'animateInHorizontal' : 'animateIn'
  );
  const [pendingStepId, setPendingStepId] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const [wrapperWidth, setWrapperWidth] = useState<number | null>(null);
  const params = useParams<{ workspaceId: string, id: string }>()
  const searchParams = useSearchParams();
  const isContinueScreen = currentStepId === 'continue-screen'
  const currentStep = leadForm.journeyItems?.find(step => step.blockId === currentStepId)
  const createConversationMutation = trpc.leadForm.createConversation.useMutation()
  const createEditorConversationMutation = trpc.leadForm.createEditorConversation.useMutation()
  const createContactMutation = trpc.contact.createContact.useMutation()
  const saveConversationEntryMutation = trpc.leadForm.saveConversationEntry.useMutation()
  const evaluateAnswerMutation = trpc.leadForm.evaluateAnswer.useMutation();
  const summarizeConversation = trpc.leadForm.summarizeConversation.useMutation()
  const totalSteps = topLevelJourneyItems.length || 0;
  const lastMediaFromJourneyItems: Media | null = leadForm.journeyItems?.slice().reverse().find(j => j.media)?.media ?? null
  // If the current step was removed from the form (e.g. AI deleted it), fall back to the first step
  useEffect(() => {
    if (
      currentStepId &&
      currentStepId !== 'exit' &&
      currentStepId !== 'continue-screen' &&
      !currentStep &&
      topLevelJourneyItems.length > 0 &&
      (editorMode || previewMode)
    ) {
      setCurrentStepId(topLevelJourneyItems[0].blockId)
    }
  }, [currentStepId, currentStep, topLevelJourneyItems])

  // Find the current index of the step (top-level only — children inside multi-question
  // pages don't count toward overall progress).
  const currentStepIndex = useMemo(() => {
    const journeyItemIndex = topLevelJourneyItems.findIndex(item => item.blockId === currentStepId);
    return journeyItemIndex !== -1 ? journeyItemIndex : 0;
  }, [currentStepId, topLevelJourneyItems]);

  // Helper function to get animation class names based on transition type
  const getAnimationClassName = (direction: 'in' | 'out') => {
    const isHorizontal = leadForm.transitionType === 'SLIDE_HORIZONTAL';
    return isHorizontal
      ? (direction === 'in' ? 'animateInHorizontal' : 'animateOutHorizontal')
      : (direction === 'in' ? 'animateIn' : 'animateOut');
  };

  const form = useForm({
    resolver: zodResolver(
      createFormSchema(leadForm.journeyItems ?? [])
    ),
    // Resume the existing in-progress conversation (when revisiting) so saved
    // answers append to it instead of spawning a duplicate conversation. A
    // fresh visit has no previousConversation, so this stays undefined and a
    // new conversation is created on the first answer as before.
    defaultValues: { ...defaultValues, conversationId: previousConversation?.id ?? undefined, consent: false } as Record<string, any>
  });
  const { conversationId, ...userResponses } = form.getValues()

  // GDPR consent logic - using useMemo for cleaner async handling
  const formRequiresGDPRConsent = useMemo(() => {
    if (leadForm.gdprSetting === 'ALWAYS') {
      return true
    } else if (leadForm.gdprSetting === 'AUTO') {
      return null
    } else {
      return false
    }
  }, [leadForm.gdprSetting])

  const autoGDPRResult = useMemo(() => {
    if (leadForm.gdprSetting !== 'AUTO') return null

    try {
      const countryCode = getCountryCode()
      return isGDPRCountry(countryCode)
    } catch (error) {
      return false
    }
  }, [leadForm.gdprSetting])

  // Final GDPR consent requirement - combines memo result with async result
  const finalFormRequiresGDPRConsent = formRequiresGDPRConsent !== null
    ? formRequiresGDPRConsent
    : autoGDPRResult

  const consentValue = form.watch('consent')
  // A consent-requiring field may live inside a multi-question page. Walk top-level
  // first; if a container holds one, surface the container's blockId so the GDPR
  // gate triggers when we reach the page.
  const firstQuestionRequiringConsent = useMemo(() => {
    const requires = (t: string) => t === 'CONTACT_INFO' || t === 'ADDRESS' || t === 'EMAIL' || t === 'PHONE_NUMBER'
    for (const item of topLevelJourneyItems) {
      if (requires(item.type)) return item.blockId
      if (item.type === 'MULTI_QUESTION_PAGE') {
        const child = (leadForm.journeyItems ?? []).find(c => c.parentId === item.id && requires(c.type))
        if (child) return item.blockId
      }
    }
    return undefined
  }, [topLevelJourneyItems, leadForm.journeyItems])
  const userStillRequiresGDPRConsent = useMemo(() => {
    const consentedBlockId = !!leadForm.journeyItems?.find(item => item.blockId === consentValue)

    // If GDPR requirement is still being determined, assume no consent required for now
    if (finalFormRequiresGDPRConsent === null || !finalFormRequiresGDPRConsent) return false

    // If no conversation exists yet, but GDPR is required, we still need consent
    // This handles the case where a consent-requiring field is the first step
    if (!conversationId && finalFormRequiresGDPRConsent) {
      return !consentedBlockId
    }

    return (conversationId && !consentedBlockId)
  }, [conversationId, finalFormRequiresGDPRConsent, consentValue, leadForm.journeyItems])

  // If the rendered `currentStep` is a child of a MULTI_QUESTION_PAGE (e.g.
  // the user just dragged a top-level question into a page), navigation needs
  // to treat the parent container as the active step — otherwise it sits
  // outside the top-level nav list and prev/next lookups throw.
  const effectiveNavStep = useMemo(() => {
    if (!currentStep) return null
    if (currentStep.parentId) {
      return leadForm.journeyItems?.find(i => i.id === currentStep.parentId) ?? null
    }
    return currentStep
  }, [currentStep, leadForm.journeyItems])

  // Back-navigation target. Prefer the actual visit history (exact — handles
  // branch logic and converging paths); fall back to deriving it from the
  // form structure when there is no usable history (e.g. resumed sessions
  // whose saved conversation predates history seeding).
  const previousJourneyItem = (() => {
    for (let i = stepHistory.length - 1; i >= 0; i--) {
      if (stepHistory[i] === effectiveNavStep?.blockId) continue
      const item = topLevelJourneyItems.find(t => t.blockId === stepHistory[i])
      if (item) return item
    }
    if (!effectiveNavStep?.id) return null
    try {
      return getPreviousJourneyItem({
        leadForm: navLeadForm,
        currentJourneyItemId: effectiveNavStep.id,
        userResponses,
      }).previousJourneyItem
    } catch {
      // e.g. the current step was deleted from the form mid-session — a dead
      // back button beats crashing the render.
      return null
    }
  })()

  // If currentStepId now points to a child (e.g. someone dragged a top-level
  // question into a MULTI_QUESTION_PAGE), catch up to the parent container so
  // the preview keeps showing the page rather than rendering the orphan child.
  useEffect(() => {
    if (currentStep?.parentId && effectiveNavStep && effectiveNavStep.blockId !== currentStepId) {
      setCurrentStepId(effectiveNavStep.blockId)
    }
  }, [currentStep?.parentId, effectiveNavStep, currentStepId])

  const handleAnswer = useCallback(async () => {
    if (editorMode && !previewMode) {
      toast.info('The middle section is only for viewing', {
        description: 'Please edit from the right sidebar, or click the play icon to test your form.'
      })
      return
    }

    if (isHandlingAnswerRef.current) return;

    isHandlingAnswerRef.current = true;

    try {
      if (!leadForm.journeyItems) {
        toast.error('There seems to be an issue with locating this form\'s journey items', {
          description: 'If this problem persists, please contact support at support@formsuite.co.'
        });
        return;
      }

      if (!currentStep) {
        toast.error('There seems to be an issue with locating this form\'s current step', {
          description: 'If this problem persists, please contact support at support@formsuite.co.'
        });
        return
      }

      // If there is no conversation state, we need to create one before we do anything else
      if (!form.getValues('conversationId')) await createConversation()

      // Multi-question page: validate each child, save one ConversationItem per child,
      // then advance to the next top-level step in one go. This is the path that gives
      // the UX win — one Continue, validate-on-submit, batch save.
      if (currentStep.type === 'MULTI_QUESTION_PAGE') {
        const children = (leadForm.journeyItems ?? [])
          .filter(i => i.parentId === currentStep.id)
          .sort((a, b) => a.order - b.order)

        // CONTACT_INFO and ADDRESS register their inputs under sub-field keys
        // (`{blockId}_first_name`, `{blockId}_email`, etc.) rather than at
        // `blockId` directly — see `createFormSchema` below. The MQP submit
        // path needs to use those same keys, otherwise validation runs against
        // a non-existent top-level field and silently fails (errors land on a
        // path no input is bound to, so nothing renders), which made the page
        // feel like a no-op when a Contact Info child was present.
        const fieldKeysForChild = (child: FormJourneyItem): string[] => {
          if (child.type === 'CONTACT_INFO') {
            return [
              `${child.blockId}_first_name`,
              `${child.blockId}_last_name`,
              `${child.blockId}_email`,
              `${child.blockId}_phone`,
              `${child.blockId}_company`,
            ]
          }
          if (child.type === 'ADDRESS') {
            return [
              `${child.blockId}_address_line_1`,
              `${child.blockId}_address_line_2`,
              `${child.blockId}_city`,
              `${child.blockId}_region`,
              `${child.blockId}_postal_code`,
              `${child.blockId}_country`,
            ]
          }
          return [child.blockId]
        }

        // Clear any stale errors on these fields so we render a fresh validation pass.
        children.forEach(c => fieldKeysForChild(c).forEach(k => form.clearErrors(k)))

        // Validate every child synchronously; collect failures so we show them all at once.
        let hasError = false
        for (const child of children) {
          const childSchema = createFormSchema([child], true)
          const keys = fieldKeysForChild(child)
          const input = Object.fromEntries(keys.map(k => [k, form.getValues(k)]))
          const result = childSchema.safeParse(input)
          if (!result.success) {
            hasError = true
            // Set the error on the actual input path so the relevant
            // sub-field highlights and renders its own message, instead of
            // dumping every issue onto `child.blockId` where it never paints.
            for (const issue of result.error.issues) {
              const path = issue.path[0]
              if (typeof path === 'string') {
                form.setError(path, { type: 'manual', message: issue.message })
              }
            }
          }
        }
        if (hasError) return

        if (!form.getValues('conversationId')) await createConversation()

        const { nextJourneyItem } = getNextJourneyItem({
          leadForm: navLeadForm,
          currentJourneyItemId: currentStep.id,
          userResponses,
          isFinalJourneyItem: (currentStepIndex === totalSteps - 1) || !!(currentStep?.endOnCompletion && !currentStep?.logicItems?.length),
        })

        if (!previewMode) {
          // Single batched request — the server handles the array natively.
          await saveConversationEntryMutation.mutateAsync({
            conversationId: form.getValues('conversationId'),
            createdAt: new Date(),
            consent: !userStillRequiresGDPRConsent,
            entries: children.map(child => {
              if (child.type === 'CONTACT_INFO') {
                return {
                  assistantResponse: child.content,
                  journeyItemId: child.id,
                  userResponse: JSON.stringify({
                    first_name: form.getValues(`${child.blockId}_first_name`),
                    last_name: form.getValues(`${child.blockId}_last_name`),
                    email: form.getValues(`${child.blockId}_email`),
                    phone: form.getValues(`${child.blockId}_phone`),
                    company: form.getValues(`${child.blockId}_company`),
                  }),
                }
              }
              if (child.type === 'ADDRESS') {
                return {
                  assistantResponse: child.content,
                  journeyItemId: child.id,
                  userResponse: JSON.stringify({
                    address_line_1: form.getValues(`${child.blockId}_address_line_1`),
                    address_line_2: form.getValues(`${child.blockId}_address_line_2`),
                    city: form.getValues(`${child.blockId}_city`),
                    region: form.getValues(`${child.blockId}_region`),
                    postal_code: form.getValues(`${child.blockId}_postal_code`),
                    country: form.getValues(`${child.blockId}_country`),
                  }),
                }
              }
              const raw = form.getValues(child.blockId)
              return {
                assistantResponse: child.content,
                journeyItemId: child.id,
                userResponse: Array.isArray(raw) ? raw : (raw ?? ''),
              }
            }),
          })
        }

        if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production' && !previewMode) {
          const conversationId = form.getValues('conversationId') as string | undefined
          for (const child of children) {
            events.answerQuestion({
              collectionId: leadForm.collectionId,
              journeyItemId: child.id,
              conversationId,
              stepType: child.type,
            })
          }
          trackPublicFormEndReached({
            collectionId: leadForm.collectionId,
            conversationId,
            currentStepType: currentStep.type,
            nextJourneyItem,
            navLeadForm,
            userResponses,
          })
        }

        if (onFinish && (nextJourneyItem === null || nextJourneyItem?.type === 'END_SCREEN' || nextJourneyItem?.type === 'REDIRECT_SCREEN')) {
          setIsFinishing(true)
          await onFinish(form.getValues())
          return
        }

        if (!nextJourneyItem) {
          setClassNameState(getAnimationClassName('out'))
          setPendingStepId('exit')
        } else {
          setClassNameState(getAnimationClassName('out'))
          pushStepHistory(currentStep.blockId)
          setPendingStepId(nextJourneyItem.blockId)
        }
        return
      }

      let userResponse = form.getValues(currentStep.blockId)

      if (currentStep.type === 'CONTACT_INFO') {
        userResponse = JSON.stringify({
          first_name: form.getValues(`${currentStep.blockId}_first_name`),
          last_name: form.getValues(`${currentStep.blockId}_last_name`),
          email: form.getValues(`${currentStep.blockId}_email`),
          phone: form.getValues(`${currentStep.blockId}_phone`),
          company: form.getValues(`${currentStep.blockId}_company`),
        })
      } else if (currentStep.type === 'ADDRESS') {
        userResponse = JSON.stringify({
          address_line_1: form.getValues(`${currentStep.blockId}_address_line_1`),
          address_line_2: form.getValues(`${currentStep.blockId}_address_line_2`),
          city: form.getValues(`${currentStep.blockId}_city`),
          region: form.getValues(`${currentStep.blockId}_region`),
          postal_code: form.getValues(`${currentStep.blockId}_postal_code`),
          country: form.getValues(`${currentStep.blockId}_country`),
        })
      }

      // Validate current step before continuing
      if (currentStep.type !== 'WELCOME_SCREEN' && currentStep.type !== 'END_SCREEN' && currentStep.type !== 'REDIRECT_SCREEN') {
        const currentStepSchema = createFormSchema([currentStep], true);

        if (currentStep.type === 'CONTACT_INFO' || currentStep.type === 'ADDRESS') {
          const validationResult = currentStepSchema.safeParse(Object.fromEntries(
            Object.entries(JSON.parse(userResponse))
              .map(([key, value]) => [`${currentStep.blockId}_${key}`, value])
          ))

          if (!validationResult.success) {
            const zodError = validationResult.error;
            zodError.issues.forEach((error: z.core.$ZodIssue) => {
              form.setError(error.path[0] as string, { type: 'manual', message: error.message });
            });

            return;
          }
        } else {
          const validationResult = currentStepSchema.safeParse({ [currentStep.blockId]: userResponse });

          if (!validationResult.success) {
            const zodError = validationResult.error;
            zodError.issues.forEach((error: z.core.$ZodIssue) => {
              form.setError(currentStep.blockId, { type: 'manual', message: error.message });
            });

            return;
          }
        }
      }

      // When GDPR consent is required and gdprRequired is true, block progression until user consents
      const isOnConsentStep = currentStep.blockId === firstQuestionRequiringConsent;
      const hasUserConsented = form.getValues('consent') === true || form.getValues('consent') === firstQuestionRequiringConsent;
      if (
        finalFormRequiresGDPRConsent &&
        leadForm.gdprRequired &&
        isOnConsentStep &&
        !hasUserConsented
      ) {
        form.setError('consent', {
          type: 'manual',
          message: 'GDPR consent is required to proceed.',
        });
        return;
      }

      // Get the next journey item — using the top-level-only nav list so multi-question
      // page children don't accidentally become standalone next-steps.
      const { nextJourneyItem } = getNextJourneyItem({
        leadForm: navLeadForm,
        currentJourneyItemId: currentStep?.id,
        userResponses: {
          ...userResponses,
          [currentStep.blockId]: userResponse,
        },
        isFinalJourneyItem: (currentStepIndex === totalSteps - 1) || !!(currentStep?.endOnCompletion && !currentStep?.logicItems?.length),
      })

      // If its a step that takes a user response, save the conversation entry
      if (currentStep.type !== 'WELCOME_SCREEN' && currentStep.type !== 'END_SCREEN' && currentStep.type !== 'REDIRECT_SCREEN') {
        if (currentStep.type === 'OPEN_ENDED') {
          const responses: ResponseMessages[] = userResponse.map((r: any) => ResponseMessages.parse(r.data))

          const result = await evaluateAnswerMutation.mutateAsync({
            conversationId: form.getValues('conversationId'),
            journeyItemId: currentStep.id,
            responseMessages: responses,
            leadForm,
          })

          if (!result.proceed) {
            const lastResponse = result.responseMessages[result.responseMessages.length - 1]
            form.setValue(currentStep.blockId, [
              ...form.getValues(currentStep.blockId),
              { data: lastResponse },
              { data: { id: uuidv4(), response: '', type: 'User', blockId: currentStep.blockId }, },
            ])
            return
          }

          if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production' && !previewMode) {
            events.answerQuestion({
              collectionId: leadForm.collectionId,
              journeyItemId: currentStep.id,
              conversationId: form.getValues('conversationId'),
              stepType: currentStep.type,
            })
          }

          if (!editorMode && (nextJourneyItem === null || nextJourneyItem?.type === 'END_SCREEN' || nextJourneyItem?.type === 'REDIRECT_SCREEN')) {
            summarizeConversation.mutateAsync({
              leadForm: leadForm,
              conversastionCompleted: true,
              conversationId: form.getValues('conversationId')
            }, {
              onSuccess: () => {
                // form.reset({
                //   ...defaultValues,
                //   conversationId: undefined
                // });

                if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production') {
                  events.finishedConversation({
                    collectionId: leadForm.collectionId,
                    contactId: userIdentifier[leadForm.workspaceId],
                    conversationId: form.getValues('conversationId')
                  })
                  // Fire the form owner's Meta Pixel / GTM conversion (the tags
                  // themselves are injected on the public form by FormTrackingScripts).
                  fireFormConversion({
                    metaPixel: leadForm.metaPixel,
                    googleTagManager: leadForm.googleTagManager,
                  })
                }
              },
              onError: (error) => Sentry.captureException(error)
            })
          }
        } else {
          const handleSaveConversationEntry = async () => {
            if (!previewMode) {
              await saveConversationEntryMutation.mutateAsync({
                conversationId: form.getValues('conversationId'),
                createdAt: new Date(),
                consent: !userStillRequiresGDPRConsent,
                entries: [{
                  assistantResponse: currentStep.content,
                  journeyItemId: currentStep.id,
                  userResponse: userResponse,
                  files: currentStep.type === 'FILE' ? userResponse.split(',') : undefined,
                }],
              }, {
                onSuccess: () => {
                  if (!editorMode && (nextJourneyItem === null || nextJourneyItem?.type === 'END_SCREEN' || nextJourneyItem?.type === 'REDIRECT_SCREEN')) {
                    summarizeConversation.mutateAsync({
                      leadForm: leadForm,
                      conversastionCompleted: true,
                      conversationId: form.getValues('conversationId')
                    }, {
                      onSuccess: () => {
                        // form.reset({
                        //   ...defaultValues,
                        //   conversationId: undefined
                        // });

                        if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production') {
                          events.finishedConversation({
                            collectionId: leadForm.collectionId,
                            contactId: userIdentifier[leadForm.workspaceId],
                            conversationId: form.getValues('conversationId')
                          })
                        }
                      },
                      onError: (error) => Sentry.captureException(error)
                    })
                  }
                }
              });
            } else {
              // onFinish is handled in the navigation guard below
            }
          }

          if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production' && !previewMode) {
            events.answerQuestion({
              collectionId: leadForm.collectionId,
              journeyItemId: currentStep.id,
              conversationId: form.getValues('conversationId'),
              stepType: currentStep.type,
            })
          }

          // If the next journey item is an open ended question, we need to save the conversation entry before proceeding
          // So the AI know the most recent response
          if (nextJourneyItem?.type === 'OPEN_ENDED') {
            await handleSaveConversationEntry()
          } else {
            handleSaveConversationEntry()
          }
        }
      }

      if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production' && !previewMode) {
        trackPublicFormEndReached({
          collectionId: leadForm.collectionId,
          conversationId: form.getValues('conversationId'),
          currentStepType: currentStep.type,
          nextJourneyItem,
          navLeadForm,
          userResponses: {
            ...userResponses,
            [currentStep.blockId]: userResponse,
          },
        })
      }

      // If onFinish is provided and the form is finishing, stay on current step with spinner
      if (onFinish && (nextJourneyItem === null || nextJourneyItem?.type === 'END_SCREEN' || nextJourneyItem?.type === 'REDIRECT_SCREEN')) {
        setIsFinishing(true)
        await onFinish(form.getValues())
        return
      }

      // If the next journey item is null (form is complete), set the current step to the exit step
      if (!nextJourneyItem) {
        setClassNameState(getAnimationClassName('out'))
        setPendingStepId('exit')
      } else {
        setClassNameState(getAnimationClassName('out'))
        pushStepHistory(currentStep.blockId)
        setPendingStepId(nextJourneyItem?.blockId)
      }
    } finally {
      isHandlingAnswerRef.current = false;
    }
  }, [leadForm.journeyItems, currentStepIndex, userStillRequiresGDPRConsent, finalFormRequiresGDPRConsent, firstQuestionRequiringConsent, pushStepHistory])

  const allowedToGoToNextJourneyItem = useMemo(() => {
    if (!currentStep) return false

    const userResponse = form.watch(currentStep.blockId)
    const currentStepSchema = createFormSchema([currentStep], true);
    const validationResult = currentStepSchema.safeParse({ [currentStep.blockId]: userResponse });

    const validationPassed = validationResult.success;

    // When GDPR consent is required and gdprRequired is true, user must check consent before proceeding
    const isOnConsentStep = currentStep.blockId === firstQuestionRequiringConsent;
    const hasUserConsented = consentValue === true || consentValue === firstQuestionRequiringConsent;
    const consentRequiredForStep = finalFormRequiresGDPRConsent && leadForm.gdprRequired && isOnConsentStep;
    const consentBlocking = consentRequiredForStep && !hasUserConsented;

    return validationPassed && !consentBlocking
  }, [currentStep?.blockId, currentStep?.blockId && form.getValues(currentStep.blockId), firstQuestionRequiringConsent, finalFormRequiresGDPRConsent, leadForm.gdprRequired, consentValue])

  // Required scheduler steps advance only after a booking — hide manual
  // next/Ok affordances so respondents can't skip the calendar widget.
  const hideSchedulerManualAdvance =
    currentStep?.type === 'SCHEDULER' && !!currentStep.manditory;

  // Calculate progress
  const progressPercentage = useMemo(() => (currentStepIndex / (totalSteps - 1)) * 100, [currentStepIndex, totalSteps]);

  // "Takes X minutes" badge shown on the form's first step, whatever its type
  // (welcome screen, or the first question when there's no welcome screen).
  // Replaces the "press Enter" hint next to that step's button. On by
  // default; form setting.
  const completionEstimate = useMemo(
    () => formatCompletionEstimate(estimateCompletionSeconds(leadForm.journeyItems)),
    [leadForm.journeyItems]
  );
  const showTimeEstimateBadge = leadForm.showTimeToComplete !== false && !!completionEstimate;
  const showTimeEstimateOnStep =
    showTimeEstimateBadge &&
    !!currentStep &&
    topLevelJourneyItems[0]?.blockId === currentStep.blockId &&
    currentStep.type !== 'END_SCREEN' &&
    currentStep.type !== 'REDIRECT_SCREEN';

  const getMediaPosition = (mediaPosition: JourneyItemMediaPosition | null): JourneyItemMediaPosition => {
    return (!mediaPosition || mediaPosition === null || mediaPosition === 'RANDOM')
      ? getRandomJourneyItemMediaPosition()
      : mediaPosition
  }

  const randomMediaPositionMap = useMemo(() => {
    const positionMap = new Map();
    leadForm.journeyItems?.forEach(item => {
      positionMap.set(item.id, getMediaPosition(item.mediaPosition));
    });
    return positionMap;
  }, [leadForm.journeyItems]);

  const goBack = (!previousJourneyItem || previousJourneyItem.type === 'WELCOME_SCREEN')
    ? undefined
    : async () => {
      if (editorMode && !previewMode) {
        toast.info('Navigating steps is not available in the editor', {
          description: 'If you would like the complete a form please use the preview mode.'
        })
        return
      }

      if (!previousJourneyItem) {
        toast.info('There seems to be an issue', {
          description: 'If this problem persists, please contact support at support@formsuite.co'
        });
        return;
      }

      setClassNameState(getAnimationClassName('in'))
      // Pop the step we're returning to (and anything above it) off the
      // history. When previous came from the structural fallback it isn't on
      // the stack and this is a no-op.
      setStepHistory(history => {
        const idx = history.lastIndexOf(previousJourneyItem.blockId)
        return idx >= 0 ? history.slice(0, idx) : history
      })
      setPendingStepId(previousJourneyItem.blockId);
    };

  // Set the CSS variables based on the form settings
  useEffect(() => {
    const rootStyle = document.documentElement.style;
    if (leadForm.backgroundColorSetting) {
      rootStyle.setProperty('--background-color', leadForm.backgroundColorSetting);
    }
    if (leadForm.textColorSetting) {
      rootStyle.setProperty('--text-color', leadForm.textColorSetting);
    }
    if (leadForm.buttonColorSetting) {
      rootStyle.setProperty('--button-color', leadForm.buttonColorSetting);
    }
    if (leadForm.buttonTextColorSetting) {
      rootStyle.setProperty('--button-text-color', leadForm.buttonTextColorSetting);
    }
    // Answer color is optional: when unset the vars are removed so every
    // consumer's `var(--answer-color, var(--button-color))`-style fallback
    // kicks in and old forms keep their button-derived answer styling.
    if (leadForm.answerColorSetting) {
      rootStyle.setProperty('--answer-color', leadForm.answerColorSetting);
      rootStyle.setProperty('--answer-text-color', contrastingTextColor(leadForm.answerColorSetting));
    } else {
      rootStyle.removeProperty('--answer-color');
      rootStyle.removeProperty('--answer-text-color');
    }
    if (leadForm.fontFamilySetting) {
      rootStyle.setProperty('--font-family', leadForm.fontFamilySetting);
    }
    if (leadForm.transitionSpeed) {
      rootStyle.setProperty('--transition-speed', `${leadForm.transitionSpeed}ms`);
    }
    if (leadForm.elementType) {
      rootStyle.setProperty(
        '--border-radius',
        leadForm.elementType === 'ROUNDED' ? '9999px' : leadForm.elementType === 'SEMI_ROUNDED' ? '6px' : '0'
      );
    }
  }, [
    leadForm.backgroundColorSetting,
    leadForm.textColorSetting,
    leadForm.buttonColorSetting,
    leadForm.buttonTextColorSetting,
    leadForm.answerColorSetting,
    leadForm.fontSizeSetting,
    leadForm.fontFamilySetting,
    leadForm.elementType,
    leadForm.transitionSpeed,
  ]);

  // Track window and wrapper width for responsive styles (SSR-safe)
  useEffect(() => {
    const updateDimensions = () => {
      setWindowWidth(window.innerWidth);
      const wrapper = document.getElementById(wrapperId);
      setWrapperWidth(wrapper?.offsetWidth ?? null);
    };

    // Initial update
    updateDimensions();

    // Listen for resize
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [wrapperId]);

  useEffect(() => {
    const rootStyle = document.documentElement.style;
    const conversationWrapper = document.getElementById(wrapperId);
    const wrapperWidth = conversationWrapper?.offsetWidth ?? 760;
    const isSmallScreen = wrapperWidth < 760;
    // Percentage to reduce all sizes by (only in editor mode)
    const SIZE_REDUCTION_PERCENTAGE = leadForm.fontSizeSetting === 'LARGE'
      ? isSmallScreen ? 0.4 : 0.3
      : isSmallScreen ? 0.4 : 0.2;

    // Helper function to reduce pixel values by the specified percentage (only if in editor mode)
    const reduceSize = (pixelValue: string): string => {
      const numericValue = parseInt(pixelValue.replace('px', ''));
      const reducedValue = editorMode || minimizeContentSize
        ? Math.round(numericValue * (1 - SIZE_REDUCTION_PERCENTAGE))
        : numericValue;
      return `${reducedValue}px`;
    };

    const updateStyles = () => {
      const wrapperWidth = conversationWrapper?.offsetWidth ?? 760;
      const isSmallScreen = wrapperWidth < 760;

      switch (leadForm.fontSizeSetting) {
        case 'EXTRA_SMALL': {
          rootStyle.setProperty('--text-size', isSmallScreen ? reduceSize('16px') : reduceSize('18px'));
          rootStyle.setProperty('--button-height', isSmallScreen ? reduceSize('40px') : reduceSize('44px'));
          rootStyle.setProperty('--button-text-size', isSmallScreen ? reduceSize('20px') : reduceSize('20px'));
          rootStyle.setProperty('--heading-gap', isSmallScreen ? reduceSize('15px') : reduceSize('18px'));
          break;
        }
        case 'MEDIUM': {
          rootStyle.setProperty('--text-size', isSmallScreen ? reduceSize('26px') : reduceSize('32px'));
          rootStyle.setProperty('--button-height', isSmallScreen ? reduceSize('48px') : reduceSize('52px'));
          rootStyle.setProperty('--button-text-size', isSmallScreen ? reduceSize('24px') : reduceSize('24px'));
          rootStyle.setProperty('--heading-gap', isSmallScreen ? reduceSize('20px') : reduceSize('32px'));
          break;
        }
        case 'LARGE': {
          rootStyle.setProperty('--text-size', isSmallScreen ? reduceSize('28px') : reduceSize('44px'));
          rootStyle.setProperty('--button-height', isSmallScreen ? reduceSize('48px') : reduceSize('56px'));
          rootStyle.setProperty('--button-text-size', isSmallScreen ? reduceSize('24px') : reduceSize('26px'));
          rootStyle.setProperty('--heading-gap', isSmallScreen ? reduceSize('23px') : reduceSize('36px'));
          break;
        }
        default: {
          rootStyle.setProperty('--text-size', isSmallScreen ? reduceSize('22px') : reduceSize('28px'));
          rootStyle.setProperty('--button-height', isSmallScreen ? reduceSize('40px') : reduceSize('44px'));
          rootStyle.setProperty('--button-text-size', isSmallScreen ? reduceSize('20px') : reduceSize('20px'));
          rootStyle.setProperty('--heading-gap', isSmallScreen ? reduceSize('17px') : reduceSize('20px'));
          break;
        }
      }

      // Welcome/end screen titles default to 170% of --text-size (the h1 CSS
      // fallback). When the form carries an explicit screenFontSizeSetting
      // (e.g. Typeform imports, where screen titles are sized independently of
      // questions), size the title from that setting's text size instead.
      const SCREEN_TITLE_BASE: Record<string, [small: string, large: string]> = {
        EXTRA_SMALL: ['16px', '18px'],
        SMALL: ['22px', '28px'],
        MEDIUM: ['26px', '32px'],
        LARGE: ['28px', '44px'],
      };
      const screenTitleBase = leadForm.screenFontSizeSetting
        ? SCREEN_TITLE_BASE[leadForm.screenFontSizeSetting]
        : null;
      if (screenTitleBase) {
        const basePx = parseInt(reduceSize(isSmallScreen ? screenTitleBase[0] : screenTitleBase[1]));
        rootStyle.setProperty('--screen-title-size', `${Math.round(basePx * 1.7)}px`);
      } else {
        rootStyle.removeProperty('--screen-title-size');
      }
    };

    const resizeObserver = new ResizeObserver(debounce(() => {
      updateStyles();
    }, 300));

    if (conversationWrapper) {
      resizeObserver.observe(conversationWrapper);
    }

    updateStyles();

    return () => {
      resizeObserver.disconnect();
    };
    // Only depend on properties that actually affect text/button sizing.
    // Color settings are handled by the separate CSS variables effect above.
  }, [
    leadForm.fontSizeSetting,
    leadForm.screenFontSizeSetting,
    leadForm.fontFamilySetting,
    windowWidth,
    wrapperWidth,
    editorMode,
    minimizeContentSize,
  ]);

  // Handle pending step changes with proper timing for animations
  useEffect(() => {
    if (pendingStepId) {
      // Small delay to ensure animation state is processed first
      const timer = setTimeout(() => {
        setCurrentStepId(pendingStepId);
        setPendingStepId(null);
      }, 10); // Very small delay, just enough for React to process the state change

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [pendingStepId]);

  useEffect(() => {
    if (currentStepOverride && currentStepOverride !== previousStepOverrideRef.current) {
      const newJourneyItem = leadForm.journeyItems?.find(item => item.blockId === currentStepOverride)
      const previousJourneyItem = leadForm.journeyItems?.find(item => item.blockId === previousStepOverrideRef.current)

      // If the override targets a child question (parentId set), coerce to the parent
      // container's blockId so navigation/preview stay on the page. The settings panel
      // still reads the raw URL param so the child's editor still opens.
      const effectiveBlockId = (() => {
        if (newJourneyItem?.parentId) {
          const parent = leadForm.journeyItems?.find(i => i.id === newJourneyItem.parentId)
          return parent?.blockId ?? currentStepOverride
        }
        return currentStepOverride
      })()

      // Determine animation direction based on order comparison
      if (previousStepOverrideRef.current && (newJourneyItem?.order ?? 0) < (previousJourneyItem?.order ?? 0)) {
        // Going backwards - use animateIn (like goBack function)
        setClassNameState(getAnimationClassName('in'))
        setPendingStepId(effectiveBlockId);
      } else if (previousStepOverrideRef.current) {
        // Going forwards - use animateOut (like handleAnswer function)
        setClassNameState(getAnimationClassName('out'))
        setPendingStepId(effectiveBlockId);
      } else {
        // First time setting currentStepOverride - no animation needed
        setCurrentStepId(effectiveBlockId);
      }
    }

    // Update the ref to store the current value for the next render
    previousStepOverrideRef.current = currentStepOverride;
  }, [currentStepOverride])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // In AI preview mode, only respond to Enter from elements INSIDE our wrapper
      // so the AI chat input (outside the wrapper) doesn't accidentally advance the form.
      if (previewMode) {
        const wrapper = document.getElementById(wrapperId);
        const el = document.activeElement;
        if (wrapper && el && el !== document.body && el instanceof Node && !wrapper.contains(el)) {
          return;
        }
        if (wrapper && event.target instanceof Node && !wrapper.contains(event.target as Node)) {
          return;
        }
      }

      // Check if Enter is pressed and Shift is not held down
      if (
        event.key === 'Enter' &&
        !event.shiftKey && (!editorMode || editorMode && previewMode) &&
        currentStep?.type !== 'END_SCREEN' && currentStep?.type !== 'REDIRECT_SCREEN' &&
        currentStepId !== 'exit' &&
        !hideSchedulerManualAdvance
      ) {
        event.preventDefault();
        handleAnswer()
      }
    };

    // Add event listener for keydown
    if (!editorMode || (editorMode && previewMode)) {
      window.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleAnswer, hideSchedulerManualAdvance, currentStep?.type, currentStepId, editorMode, previewMode, wrapperId]);

  function determineNextStep() {
    const firstItem = topLevelJourneyItems[0]
    const secondItem = topLevelJourneyItems[1]

    return currentStepOverride ?? revisitingConversation
      ? 'continue-screen'
      : !leadForm.skipWelcomeScreen
        ? firstItem?.blockId
        : (firstItem?.type === 'WELCOME_SCREEN' || firstItem?.type === 'END_SCREEN' || firstItem?.type === 'REDIRECT_SCREEN')
          ? secondItem?.blockId
          : firstItem?.blockId
  }

  const hiddenFields = leadForm.hiddenFields?.map(field => {
    const value = searchParams?.get(field);
    return value ? { field, value: String(value) } : null;
  }).filter(Boolean) as { field: string; value: string }[];

  async function createConversation(conversationId?: string): Promise<Conversation> {
    if (previewMode) {
      const randomId = uuidv4()
      form.setValue('conversationId', randomId)

      return {
        id: randomId,
        contactId: uuidv4(),
        createdAt: new Date(),
        updatedAt: new Date(),
        formId: leadForm.id,
        status: 'IN_PROGRESS',
        // hiddenFields: leadForm.hiddenFields ?? [],
        // TODO: Add hidden fields
        hiddenFields: [],
        expense: 0,
        customScore: null,
        referral: null,
        sentiment: null,
        seen: false,
        country: country ?? null,
        threadId: uuidv4(),
        referrer: null,
        conversationItem: [],
        score: null,
        summary: null,
        consent: false,
        requiresGDPRConsent: finalFormRequiresGDPRConsent ?? false,
      }
    }

    // If there is no conversation state saved yet, we need to create one
    if (leadForm && !form.getValues('conversationId') && !createContactMutation.isPending) {
      if (editorMode) {
        if (params?.id && currentStepOverride === null) {
          const data = await createEditorConversationMutation.mutateAsync({
            collectionId: params?.id,
          }, {
            onError: (error) => {
              toast.error('There seems to be an issue, please refresh and try again', {
                description: 'If the problem persists, please contact support.'
              })
              Sentry.captureException(error)
            }
          })

          form.setValue('conversationId', data.id)

          return data
        } else {
          Sentry.captureException(new AppError({
            message: 'Collection ID not found in editor',
            code: 'NOT_FOUND'
          }))
        }
      } else {
        // If we already have a userIdentifier for this workspace, use it
        if (userIdentifier[leadForm.workspaceId]) {
          const data = await createConversationMutation.mutateAsync({
            formId: leadForm.id,
            contactId: userIdentifier[leadForm.workspaceId],
            referral: document.referrer,
            conversationId,
            hiddenFields,
            requiresGDPRConsent: finalFormRequiresGDPRConsent ?? false,
            country: country ?? null,
          }, {
            onError: (error) => {
              toast.error('There seems to be an issue, please refresh and try again', {
                description: 'If the problem persists, please contact support.'
              })
              Sentry.captureException(error)
            }
          })

          form.setValue('conversationId', data.id)

          if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production') {
            events.createConversation({
              collectionId: leadForm.collectionId,
              contactId: userIdentifier[leadForm.workspaceId],
              conversationId: data.id
            })
          }

          return data
          // Create a new user identifier for the workspace
        } else {
          const _contact = await createContactMutation.mutateAsync({
            workspaceId: leadForm.workspaceId,
          })

          if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production') {
            events.createContact({ workspaceId: leadForm.workspaceId, contactId: _contact.id })
          }

          const data = await createConversationMutation.mutateAsync({
            formId: leadForm.id,
            contactId: _contact.id,
            referral: document.referrer,
            hiddenFields,
            requiresGDPRConsent: finalFormRequiresGDPRConsent ?? false,
            country: country ?? null,
          }, {
            onError: (error) => {
              toast.error('There seems to be an issue, please refresh and try again', {
                description: 'If the problem persists, please contact support.'
              })
              Sentry.captureException(error)
            }
          })

          form.setValue('conversationId', data.id)
          setUserIdentifier({ ...userIdentifier, [leadForm.workspaceId]: _contact.id })
          if (!editorMode && env.NEXT_PUBLIC_APP_ENV === 'production') {
            events.createConversation({
              collectionId: leadForm.collectionId,
              contactId: _contact.id,
              conversationId: data.id
            })
          }

          return data
        }
      }
    }

    throw new AppError({
      message: 'No conversation created',
      code: 'INTERNAL_SERVER_ERROR'
    })
  }

  return (
    <div
      className={cn(
        'conversationWrapper',
        // Inline-height mode only applies in the LIVE form. The editor preview
        // and the in-app preview render inside fixed-height panels, where the
        // form needs to keep its internal scroll — otherwise the surrounding
        // chrome and the page itself would have to scroll instead.
        !editorMode && !previewMode &&
        currentStep?.type === 'MULTI_QUESTION_PAGE' && currentStep?.forthContent === 'inline' &&
        'inline-height',
        // Without the "Built with Formsuite" badge, the mobile footer's
        // bottom-spacing reservation (50px for the badge area) leaves a
        // visibly empty gap below the submit button. Tag the wrapper so CSS
        // can tighten that offset.
        leadForm.hideBuiltWithBadge && 'noBuiltWithBadge'
      )}
      data-ph-no-capture
    >
      <div id={wrapperId} className="editorWrapper">
        <AnimatePresence initial={false}>
          {
            leadForm.closed
              ? (
                <ConversationStepWrapper
                  media={undefined}
                  mediaPosition={'FULL'}
                  mobileMediaPosition={'MOBILE_FULL'}
                  mediOpacity={60}
                  progress={+progressPercentage.toFixed(2)}
                  contentAlignment="CENTER"
                  currentStepType="WELCOME_SCREEN"
                  duration={leadForm.transitionSpeed ?? 500}
                >
                  <Form form={form} autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                    <div
                      className={cn(
                        'screenRenderer',
                        leadForm.type?.toLowerCase() ?? 'multi_step',
                        'centered',
                        'c-content-center',
                        editorMode && 'editorMode',
                        minimizeContentSize && 'minimizeContentSize',
                        !!leadForm.logoUrl && 'withLogoSpacing',
                        !!leadForm.logoUrl && logoPosition.startsWith('BOTTOM') && 'logoAtBottom',
                      )}
                    >
                      <div className="contentInner">
                        <div className="content">
                          <ConversationWelcomeScreen
                            id="closedScreen"
                            key="closedScreen"
                            type="closed"
                            about={leadForm.about}
                            purpose={leadForm.overview}
                            tone={leadForm.tone}
                            snippet={leadForm.snippets?.map(snippet => snippet.content) ?? []}
                            title={leadForm.closedMessage?.length ? leadForm.closedMessage : 'Sorry this conversation has been closed'}
                            caption={null}
                            leadForm={leadForm}
                          />
                        </div>
                      </div>
                    </div>
                  </Form>
                </ConversationStepWrapper>
              )
              : (
                <ConversationStepWrapper
                  editorMode={editorMode}
                  progress={+progressPercentage.toFixed(2)}
                  currentStepType={currentStep?.type}
                  duration={leadForm.transitionSpeed ?? 500}
                  highFetchPriority={currentStep?.type === 'WELCOME_SCREEN'}
                  media={isContinueScreen
                    ? null
                    : currentStepId === 'exit'
                      ? lastMediaFromJourneyItems
                      : currentStep && currentStep.media ? Media.parse(currentStep.media) : null
                  }
                  mediaPosition={
                    isContinueScreen
                      ? 'FULL'
                      : currentStep && currentStep.mediaPosition ? currentStep.mediaPosition : randomMediaPositionMap.get(currentStepId)
                  }
                  mobileMediaPosition={
                    isContinueScreen
                      ? 'MOBILE_FULL'
                      : currentStep && currentStep.mobileMediaPosition ? currentStep.mobileMediaPosition : 'MOBILE_FULL'
                  }
                  mediOpacity={
                    isContinueScreen
                      ? 60
                      : currentStep && currentStep.opacity ? currentStep.opacity : 60
                  }
                  contentAlignment={
                    isContinueScreen
                      ? 'CENTER'
                      : currentStep && currentStep.contentAlignment ? currentStep.contentAlignment : 'CENTER'
                  }
                >
                  {
                    isContinueScreen && (
                      <div className="default-screen">
                        <ContinueScreen
                          leadForm={leadForm}
                          setCurrentStepId={setCurrentStepId}
                          previousConversation={previousConversation}
                          resetForm={(conversationId?: string) => {
                            // Starting over discards the resumed session's visit history.
                            if (!conversationId) setStepHistory([])
                            form.reset({
                              ...(!conversationId ? resetDefaultValuesToBlank(leadForm.journeyItems ?? []) : defaultValues),
                              conversationId
                            });
                          }}
                        />
                      </div>
                    )
                  }
                  {
                    !isContinueScreen &&
                    <Form form={form} autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                      <SwitchTransition mode="out-in">
                        <CSSTransition
                          key={currentStepId === 'exit' ? 'exit' : currentStep?.blockId}
                          nodeRef={transitionNodeRef}
                          timeout={leadForm.transitionSpeed ?? 500}
                          classNames={classNameState}
                        >
                          <div ref={transitionNodeRef}>
                            {
                              currentStepId === 'exit' ? (
                                <ScreenRenderer
                                  key='exit'
                                  wrapperId={wrapperId}
                                  currentStep={{
                                    id: 'exit',
                                    blockId: 'exit',
                                    content: 'Thanks for completing this form',
                                    secondaryContent: `Try creating your own beautiful form today. It's easy, fun and free.`,
                                    thirdContent: 'Create a form today',
                                    forthContent: 'https://www.formsuite.co',
                                    consentTitle: null,
                                    contentAlignment: 'CENTER',
                                    mediaPosition: 'FULL',
                                    mobileMediaPosition: 'MOBILE_FULL',
                                    type: 'END_SCREEN',
                                    createdAt: new Date(),
                                    updatedAt: new Date(),
                                    formId: leadForm.id,
                                    opacity: 60,
                                    order: leadForm.journeyItems?.length ?? 1,
                                    icon: null,
                                    internalName: null,
                                    manditory: false,
                                    horizontalAlignment: false,
                                    maxLabel: null,
                                    maxRange: null,
                                    minLabel: null,
                                    minRange: null,
                                    multiSelect: null,
                                    responseTableVisible: null,
                                    strictness: 'COMMON',
                                    targetBlockId: null,
                                    youtubeVideoURL: null,
                                    additionalSettings: null,
                                    endOnCompletion: true,
                                  }}
                                  leadForm={leadForm}
                                  editorMode={!!editorMode}
                                  minimizeContentSize={minimizeContentSize}
                                  previewMode={!!previewMode}
                                  currentStepOverride={currentStepOverride}
                                  viewed={true}
                                  createConversation={createConversation}
                                  withLogoSpacing={!!leadForm.logoUrl}
                                  onContentClick={onContentClick}
                                  evaluationIsProcessing={false}
                                  handleAnswer={handleAnswer}
                                  hiddenFields={(editorMode || previewMode) ? (leadForm.hiddenFields?.map(field => ({ field, value: '' })) ?? []) : hiddenFields}
                                  requiresGDPRConsent={undefined}
                                  resetForm={() => {
                                    form.reset({
                                      ...defaultValues,
                                      conversationId: undefined
                                    });
                                  }}
                                  position='active'
                                >
                                  <div
                                    className={cn('stepFooterWrapper', 'stepFooterWrapperCentered')}
                                  // exit={{ opacity: 0, transform: 'translateY(-10px)' }}
                                  // initial={{ opacity: 0, transform: 'translateY(10px)' }}
                                  // animate={{ opacity: 1, transform: 'translateY(0)' }}
                                  // transition={{ duration: 0.5, ease: 'easeInOut' }}
                                  >
                                    <span />
                                  </div>
                                </ScreenRenderer>
                              ) : currentStep ? (
                                <ScreenRenderer
                                  key={currentStep.blockId}
                                  wrapperId={wrapperId}
                                  currentStep={currentStep}
                                  leadForm={leadForm}
                                  editorMode={!!editorMode}
                                  minimizeContentSize={minimizeContentSize}
                                  previewMode={!!previewMode}
                                  currentStepOverride={currentStepOverride}
                                  // viewed={viewedJourneyItems.includes(currentStep.blockId) || index === 0 || editorMode}
                                  viewed={false}
                                  createConversation={createConversation}
                                  withLogoSpacing={!!leadForm.logoUrl}
                                  onContentClick={onContentClick}
                                  evaluationIsProcessing={evaluateAnswerMutation.isPending}
                                  handleAnswer={handleAnswer}
                                  hiddenFields={(editorMode || previewMode) ? (leadForm.hiddenFields?.map(field => ({ field, value: '' })) ?? []) : hiddenFields}
                                  requiresGDPRConsent={finalFormRequiresGDPRConsent ? firstQuestionRequiringConsent : undefined}
                                  resetForm={() => {
                                    form.reset({
                                      ...defaultValues,
                                      conversationId: undefined
                                    });
                                  }}
                                  position='active'
                                >
                                  {
                                    // Mobile only (CSS-gated): the time estimate renders in
                                    // flow, directly under the step's last content element
                                    // (fields/caption). The sticky mobile footer button
                                    // carries no badge.
                                    showTimeEstimateOnStep && (
                                      <div className="c-time-estimate-inline">
                                        <Icon name="clock" className="shrink-0 fill-current" />
                                        <span>{completionEstimate}</span>
                                      </div>
                                    )
                                  }
                                  <div
                                    className={cn(
                                      'stepFooterWrapper',
                                      (currentStep.type === 'WELCOME_SCREEN' || currentStep.type === 'END_SCREEN' || currentStep.type === 'REDIRECT_SCREEN') && 'stepFooterWrapperCentered'
                                    )}
                                  // exit={{ opacity: 0, transform: 'translateY(-10px)' }}
                                  // initial={{ opacity: 0, transform: 'translateY(10px)' }}
                                  // animate={{ opacity: 1, transform: 'translateY(0)' }}
                                  // transition={{ duration: 0.5, ease: 'easeInOut' }}
                                  >
                                    {
                                      currentStep.type !== 'END_SCREEN' &&
                                      currentStep.type !== 'REDIRECT_SCREEN' &&
                                      !hideSchedulerManualAdvance &&
                                      <div
                                        className={cn(
                                          'c-button c-submit c-submit-to-skip',
                                          showTimeEstimateOnStep && 'c-submit-no-hint'
                                        )}
                                        onClick={() => {
                                          if (!isFinishing) handleAnswer()
                                        }}
                                      >
                                        {
                                          isFinishing
                                            ? (
                                              <span
                                                className="m-0 inline-block size-[0.85em] shrink-0 rounded-full border-2 border-solid border-current border-t-transparent animate-spin"
                                                role="status"
                                                aria-label="Loading"
                                              />
                                            )
                                            : (currentStep.type === 'WELCOME_SCREEN')
                                              ? <span>{currentStep.thirdContent ?? 'Start'}</span>
                                              : (
                                                <>
                                                  <Icon
                                                    className="!size-[0.85em] shrink-0 fill-current"
                                                    name="check"
                                                  />
                                                  <span>Ok</span>
                                                </>
                                              )
                                        }
                                        {
                                          showTimeEstimateOnStep && !isFinishing && (
                                            <span className="c-time-estimate">
                                              <Icon name="clock" className="shrink-0 fill-current" />
                                              <span>{completionEstimate}</span>
                                            </span>
                                          )
                                        }
                                      </div>
                                    }
                                  </div>
                                </ScreenRenderer>
                              ) : <span />
                            }
                          </div>
                        </CSSTransition>
                      </SwitchTransition>
                    </Form>
                  }
                  {
                    previousJourneyItem &&
                    currentStep &&
                    leadForm.journeyItems &&
                    leadForm.journeyItems.length > 1 &&
                    currentStep?.type !== 'END_SCREEN' && currentStep?.type !== 'REDIRECT_SCREEN' &&
                    currentStep?.type !== 'WELCOME_SCREEN' &&
                    <ConversationPagination
                      leadForm={leadForm}
                      previewMode={!!previewMode}
                      currentStep={currentStep}
                      goBack={goBack}
                      handleAnswer={handleAnswer}
                      previousJourneyItem={previousJourneyItem}
                      allowedToGoToNextJourneyItem={allowedToGoToNextJourneyItem}
                      hideNextButton={hideSchedulerManualAdvance}
                      theme={leadForm.elementType}
                    />
                  }

                  {
                    (
                      currentStep?.type !== 'END_SCREEN' && currentStep?.type !== 'REDIRECT_SCREEN'
                      && !isContinueScreen
                      && !leadForm.closed
                      && currentStepId !== 'exit'
                    ) &&
                    <motion.div
                      data-mobile-footer
                      className="stepFooterWrapperMobile"
                      exit={{ opacity: 0, transform: 'translateY(-10px)' }}
                      initial={{ opacity: 0, transform: 'translateY(10px)' }}
                      animate={{ opacity: 1, transform: 'translateY(0)' }}
                      transition={{ duration: 0.5, ease: 'easeInOut' }}
                    >
                      <div
                        className={cn(
                          'c-button c-square c-back',
                          (!previousJourneyItem || previousJourneyItem.type === 'WELCOME_SCREEN') && 'hidden'
                        )}
                        onClick={goBack}
                      >
                        <Icon
                          className="!size-[20px] shrink-0 fill-current rotate-90"
                          name="chevron"
                        />
                      </div>
                      {!hideSchedulerManualAdvance && (
                        <div
                          className={cn('c-button c-submit c-submit-to-skip')}
                          onClick={() => {
                            if (!isFinishing) handleAnswer()
                          }}
                        >
                          {isFinishing
                            ? (
                              <span
                                className="m-0 inline-block size-[0.85em] shrink-0 rounded-full border-2 border-solid border-current border-t-transparent animate-spin"
                                role="status"
                                aria-label="Loading"
                              />
                            )
                            : (
                              <>
                                {currentStep?.type !== 'WELCOME_SCREEN' && (
                                  <Icon
                                    className="!size-[0.85em] shrink-0 fill-current"
                                    name="check"
                                  />
                                )}
                                <span>{currentStep?.type === 'WELCOME_SCREEN' ? (currentStep.thirdContent ?? 'Start') : 'Ok'}</span>
                              </>
                            )
                          }
                        </div>
                      )}
                    </motion.div>
                  }
                </ConversationStepWrapper>
              )
          }
        </AnimatePresence>
      </div>
      {
        !leadForm.hideBuiltWithBadge &&
        <motion.a
          key="built-with"
          className={cn(
            'builtWith',
            (!leadForm.closed && leadForm.legalDisclaimer && !!leadForm.legalMessage?.length) && 'builtWithTopLayout'
          )}
          exit={{ opacity: 0, transform: 'translateY(-30px)' }}
          initial={{ opacity: 0, transform: 'translateY(30px)' }}
          animate={{ opacity: 1, transform: 'translateY(0)' }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          href="https://formsuite.co/?utm_source=formsuite&utm_medium=badge&utm_campaign=built_with&utm_content=free_form_badge"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Built with Formsuite (opens in a new tab)"
          onClick={() => {
            if (editorMode || previewMode || aiPreviewMode) return;
            events.builtWithBadgeClicked({
              workspaceId: leadForm.workspaceId,
              collectionId: leadForm.id,
            });
          }}
        >
          <p>Built with&nbsp;</p>
          <Logo darkModeOverride={true} />
          <p className="builtWithLogoText">&nbsp;Formsuite</p>
        </motion.a>
      }
      {
        !leadForm.closed && leadForm.legalDisclaimer && !!leadForm.legalMessage?.length &&
        <div className="disclaimer">
          <motion.div
            key="disclaimer"
            exit={{ opacity: 0, transform: 'translateY(-30px)' }}
            initial={{ opacity: 0, transform: 'translateY(30px)' }}
            animate={{ opacity: 1, transform: 'translateY(0)' }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <p>{leadForm.legalMessage}</p>
          </motion.div>
        </div>
      }
      {
        leadForm.logoUrl &&
        <motion.img
          key="logo"
          className={cn(
            'logo',
            `logo-${logoPosition.toLowerCase()}`,
            (editorMode || minimizeContentSize) && 'editorModeLogo',
            (minimizeContentSize && previewMode && !editorMode && !aiPreviewMode) && 'ignoreLogoPlacement'
          )}
          src={leadForm.logoUrl}
          alt={`${leadForm.name} Logo`}
          exit={{ opacity: 0, transform: 'opacity(0)' }}
          initial={{ opacity: 0, transform: 'opacity(0)' }}
          animate={{ opacity: 1, transform: 'opacity(1)' }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        />
      }
    </div >
  )
}

export default ConversationWrapper

export const getRandomJourneyItemMediaPosition = (): JourneyItemMediaPosition => {
  const positions: JourneyItemMediaPosition[] = ['RANDOM', 'DETATCHED_LEFT', 'DETATCHED_RIGHT', 'SPLIT_LEFT', 'SPLIT_RIGHT', 'FULL'];

  const randomIndex = Math.floor(Math.random() * positions.length);
  return positions[randomIndex];
}

// PhoneNumberInput stores E.164 (`+…`) values which get strict libphonenumber
// validation; anything else (legacy answers, prefilled defaults) falls back to
// the historical loose pattern so old data keeps validating.
const isValidPhoneValue = (val: string): boolean => {
  const trimmed = val.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('+')) return isValidPhoneNumber(trimmed);
  return new RegExp(phoneRegex.source).test(trimmed);
};

// URL-constructor based check: accepts real-world URLs (dashed paths, query
// strings) the old regex rejected, while still requiring a dotted hostname
// with an alphabetic TLD.
const isValidWebsiteValue = (val: string): boolean => {
  const trimmed = val.trim();
  if (!trimmed) return false;
  const candidate = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(url.hostname);
  } catch {
    return false;
  }
};

const createFormSchema = (items: FormJourneyItem[], manuallyValidated = false) => {
  const initialFields = {
    conversationId: z.string().min(1, { message: "Conversation ID is required" }),
  };

  const dynamicFields = items.reduce((acc, item) => {
    // The multi-question page container is a layout step; it has no field of its own,
    // its children are registered individually elsewhere in this reduce.
    if (item.type === 'MULTI_QUESTION_PAGE') return acc;

    let fieldSchema;
    if (item.type === 'EMAIL') {
      fieldSchema = item.manditory
        ? z.string().email({ message: "Please enter a valid email address" }).min(1, { message: "Email is required" })
        : z.string().optional().refine((val) => {
          return val === '' || z.string().email().safeParse(val).success;
        }, { message: "Please enter a valid email address" });
    } else if (item.type === 'PHONE_NUMBER') {
      fieldSchema = item.manditory
        ? z.string()
          .min(1, { message: "Please enter a phone number" })
          .refine(isValidPhoneValue, { message: "Please enter a valid phone number" })
        : z.string()
          .refine((val) => !val || isValidPhoneValue(val), { message: "Please enter a valid phone number" })
          .optional()
    } else if (item.type === 'WEBSITE') {
      fieldSchema = item.manditory
        ? z.string()
          .min(1, { message: "Please enter a website" })
          .refine(isValidWebsiteValue, { message: "Please enter a valid website" })
        : z.string()
          .refine((val) => !val || isValidWebsiteValue(val), { message: "Please enter a valid website" })
          .optional()
    } else if (item.type === 'NUMBER') {
      fieldSchema = item.manditory
        ? z.string().regex(numberRegex, { message: "Please enter a valid number" })
        : z.string().refine((val) => val === '' || z.string()
          .regex(numberRegex).safeParse(val).success, { message: "Please enter a valid number", })
          .optional()
    } else if (item.type === 'MULTIPLE_CHOICE') {
      if (item.multiSelect) {
        fieldSchema = item.manditory
          ? z.array(z.string()).min(1, { message: "Please select at least one option" })
          : z.array(z.string());
      } else {
        fieldSchema = item.manditory
          ? z.string().min(1, { message: "Please select an option" })
          : z.string().refine(val => val === '' || z.string().min(1).safeParse(val).success, {
            message: "Please select an option",
          }).optional();
      }
    } else if (item.type === 'OPEN_ENDED') {
      fieldSchema = item.manditory
        ? z.array(z.object({ data: ResponseMessages })).min(1, { message: "Please provide at least one response" })
        : z.array(z.object({ data: ResponseMessages }));
    } else if (item.type === 'DROPDOWN' || item.type === 'OPTION_SCALE' || item.type === 'RATING' || item.type === 'NPS') {
      fieldSchema = item.manditory
        ? z.string().min(1, { message: "Please select a value" })
        : z.string().refine(val => val === '' || z.string().min(1).safeParse(val).success, {
          message: "Please select a value",
        }).optional();
    } else if (item.type === 'DATE') {
      fieldSchema = item.manditory
        ? z.string().min(1, { message: "Please select a date" })
        : z.string().refine(val => val === '' || z.string().min(1).safeParse(val).success, {
          message: "Please select a date",
        }).optional();
    } else if (item.type === 'FILE') {
      fieldSchema = item.manditory
        ? z.string().min(1, { message: "Please include a file" })
        : z.string().refine(val => val === '' || z.string().min(1).safeParse(val).success, {
          message: "Please include a file",
        }).optional();
    } else if (item.type === 'SCHEDULER') {
      fieldSchema = item.manditory
        ? z.string().min(1, { message: "Please book a time before continuing" })
        : z.string().optional();
    } else {
      fieldSchema = item.manditory
        ? z.string().min(1, { message: "Please enter a text" })
        : z.string().refine(val => val === '' || z.string().min(1).safeParse(val).success, {
          message: "Please enter a text",
        }).optional();
    }

    if (item.type === 'CONTACT_INFO') {
      acc[`${item.blockId}_first_name`] = item.additionalSettings?.first_name?.required && item.additionalSettings?.first_name?.visible
        ? z.string().min(1, { message: "Please enter a first name" })
        : z.string().nullish()

      acc[`${item.blockId}_last_name`] = item.additionalSettings?.last_name?.required && item.additionalSettings?.last_name?.visible
        ? z.string().min(1, { message: "Please enter a last name" })
        : z.string().nullish()

      acc[`${item.blockId}_phone`] = item.additionalSettings?.phone?.required && item.additionalSettings?.phone?.visible
        ? z.string()
          .min(1, { message: "Please enter a phone number" })
          .refine(isValidPhoneValue, { message: "Please enter a valid phone number" })
        : z.string().nullish()
          .refine((val) => !val || isValidPhoneValue(val), { message: "Please enter a valid phone number" })

      acc[`${item.blockId}_email`] = item.additionalSettings?.email?.required && item.additionalSettings?.email?.visible
        ? z.string().email({ message: "Please enter a valid email address" })
        : z.string().nullish()
          .refine((val) => !val || z.string().email().safeParse(val).success, { message: "Please enter a valid email address" })

      acc[`${item.blockId}_company`] = item.additionalSettings?.company?.required && item.additionalSettings?.company?.visible
        ? z.string().min(1, { message: "Please enter a company" })
        : z.string().nullish()
    } else if (item.type === 'ADDRESS') {
      acc[`${item.blockId}_address_line_1`] = item.additionalSettings?.address_line_1?.required && item.additionalSettings?.address_line_1?.visible
        ? z.string().min(1, { message: "Please enter an address line" })
        : z.string().nullish()

      acc[`${item.blockId}_address_line_2`] = item.additionalSettings?.address_line_2?.required && item.additionalSettings?.address_line_2?.visible
        ? z.string().min(1, { message: "Please enter an address line" })
        : z.string().nullish()

      acc[`${item.blockId}_city`] = item.additionalSettings?.city?.required && item.additionalSettings?.city?.visible
        ? z.string().min(1, { message: "Please enter a city" })
        : z.string().nullish()

      acc[`${item.blockId}_region`] = item.additionalSettings?.region?.required && item.additionalSettings?.region?.visible
        ? z.string().min(1, { message: "Please enter a region" })
        : z.string().nullish()

      acc[`${item.blockId}_postal_code`] = item.additionalSettings?.postal_code?.required && item.additionalSettings?.postal_code?.visible
        ? z.string().min(1, { message: "Please enter a postal code" })
        : z.string().nullish()

      acc[`${item.blockId}_country`] = item.additionalSettings?.country?.required && item.additionalSettings?.country?.visible
        ? z.string().min(1, { message: "Please enter a country" })
        : z.string().nullish()
    } else {
      acc[item.blockId] = fieldSchema;
    }

    return acc;
  }, {} as Record<string, any>)

  const fullSchema = z.object({
    ...(manuallyValidated ? {} : initialFields),
    ...dynamicFields,
    consent: z.boolean().optional(),
  });

  return fullSchema;
};